import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

import { withSiteServedAdWrapper } from 'site-modules/shared/components/native-ad/site-served-ad-wrapper/site-served-ad-wrapper';
import {
  AdComponentDefaultProps,
  AdComponentPropTypes,
  SiteServedAdDefaultProps,
  SiteServedAdPropTypes,
} from 'site-modules/shared/components/ad-unit/ad-unit-prop-types';
import {
  SegmentListingCreativeConfigDefaultProps,
  SegmentListingCreativeConfigPropTypes,
} from 'client/data/models/native-ads-creative-config';
import { addZipCode, definePlatform } from 'site-modules/shared/components/native-ad/utils/utils';
import classnames from 'classnames';
import Card from 'reactstrap/lib/Card';
import { DfpTracking } from 'site-modules/shared/components/native-ad/utils/dfp-tracking';
import { AdLabel } from 'site-modules/shared/components/native-ad/native-ad-components/ad-label/ad-label';
import { CtaButtonText } from 'site-modules/shared/components/native-ad/native-ad-components/cta-button-text/cta-button-text';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { siteServedAdTrackingWrapper } from 'site-modules/shared/components/native-ad/site-served-ad-tracking-wrapper/site-served-ad-tracking-wrapper';
import { SEGMENT_LISTING_AD } from 'site-modules/shared/components/native-ad/utils/constants';
import { Disclaimer } from 'site-modules/shared/components/native-ad/native-ad-components/disclaimer/disclaimer';

import './filmstrip-type-ranking-featured.scss';

function FilmstripTypeRankingFeaturedUI({
  adRef,
  adClickTrackingListener,
  adLoadTrackingListener,
  adTrackers,
  mobile,
  creativeConfig,
  position,
  currentVehicle,
  wrapperClass,
  ivtClassName,
  inlineStyle,
  cardClass,
}) {
  const {
    siteServedAdsUpdater,
    zipCode,
    creativeConfigData: {
      logo,
      photo,
      headline,
      accolades,
      cta,
      linkDisclaimer,
      disclaimerCopy,
      lineItemId,
      siteServedCreativeId,
      linkDisplayUrl,
      target,
    },
    traffickingData: { clickTracker },
  } = creativeConfig;

  const loadTrackingListener = useCallback(
    event => {
      adLoadTrackingListener(event, cta);
    },
    [adLoadTrackingListener, cta]
  );

  const ctaURL = addZipCode(clickTracker[definePlatform(mobile)], zipCode);
  const ctaTarget = target || '_blank';

  const nativeStyle = SEGMENT_LISTING_AD.NATIVE_STYLE;

  return (
    <div className={classnames(wrapperClass, ivtClassName)} style={inlineStyle}>
      <Card
        className={classnames(
          'filmstrip-type-ranking-featured border-0 rounded-16 overflow-hidden mx-auto pos-r',
          cardClass
        )}
        data-tracking-parent={nativeStyle}
        data-viewability={`${nativeStyle}-${position}`}
        innerRef={adRef}
      >
        <DfpTracking
          lineItemId={lineItemId}
          siteServedCreativeId={siteServedCreativeId}
          mobile={mobile}
          nativeStyle={nativeStyle}
          position={position}
          slotRenderEndListener={loadTrackingListener}
          siteServedAdsUpdater={siteServedAdsUpdater}
          currentVehicle={currentVehicle}
        />
        {adTrackers}
        <a
          href={ctaURL}
          target={ctaTarget}
          rel="noopener noreferrer"
          className="site-served-ad-link no-focus text-decoration-none h-100"
          onClick={adClickTrackingListener}
        >
          <div className="mx-0 d-flex flex-column flex-lg-row">
            <figure
              className="jellybean mx-0 my-0 d-flex align-items-center overflow-hidden"
              style={{
                aspectRatio: '16 / 9',
                flex: '1 0 45%',
                maxWidth: '527px',
                minWidth: 'min(100%, 440px)',
              }}
            >
              <img src={photo} alt="" loading="lazy" className="photo w-100" />
            </figure>
            <div
              className="content pos-r px-1 py-2 py-lg-1 d-flex flex-column justify-content-center text-center"
              style={{
                flex: '1 0 50%',
              }}
            >
              <div className="logo w-100 pos-a overflow-hidden" style={{ top: '12px', left: '12px', height: '20px' }}>
                <img
                  src={logo}
                  alt=""
                  loading="lazy"
                  className="logo left-0 pos-a center-y"
                  style={{ height: '20px' }}
                />
              </div>
              <AdLabel
                styles={{
                  top: '12px',
                  right: '12px',
                }}
              />
              <div className="size-12 mb-0_5 text-blue-50 fw-bold">FEATURED NEW VEHICLE</div>
              <ContentFragment classes={'mb-1 headline text-cool-gray-30'}>{headline}</ContentFragment>
              <div className="text-cool-gray-30 mb-2 mx-auto" style={{ maxWidth: '408px' }}>
                {!!accolades?.length && accolades.join(' ')}
              </div>
              <div>
                <div className="btn size-16 btn-sm bg-white btn-outline-blue-50 px-3_5">
                  <CtaButtonText text="Learn More" />
                </div>
                <div className="my-0_25 text-blue-50 small fw-medium cta-small text-md-center">{linkDisplayUrl}</div>
              </div>
            </div>
          </div>
        </a>
        {disclaimerCopy && (
          <Disclaimer
            linkDisclaimer={linkDisclaimer}
            disclaimerText={disclaimerCopy}
            buttonClasses="pos-a bottom-0 right-0"
          />
        )}
      </Card>
    </div>
  );
}

FilmstripTypeRankingFeaturedUI.propTypes = {
  ...SiteServedAdPropTypes,
  creativeConfig: SegmentListingCreativeConfigPropTypes,
  wrapperClass: PropTypes.string,
  inlineStyle: PropTypes.shape({}),
};

FilmstripTypeRankingFeaturedUI.defaultProps = {
  ...SiteServedAdDefaultProps,
  creativeConfig: SegmentListingCreativeConfigDefaultProps,
  wrapperClass: null,
  inlineStyle: null,
};

export const FilmstripTypeRankingFeaturedSiteServedAd = siteServedAdTrackingWrapper(FilmstripTypeRankingFeaturedUI);
const FilmstripTypeRankingFeaturedWrapper = withSiteServedAdWrapper(FilmstripTypeRankingFeaturedSiteServedAd);

export function FilmstripTypeRankingFeatured(props) {
  const { type, ...restProps } = props;
  const currentVehicle = type ? { type: { niceName: type } } : null;

  return (
    <FilmstripTypeRankingFeaturedWrapper {...restProps} currentVehicle={currentVehicle} adType={SEGMENT_LISTING_AD} />
  );
}

FilmstripTypeRankingFeatured.propTypes = {
  ...AdComponentPropTypes,
  type: PropTypes.string,
  cardClass: PropTypes.string,
};

FilmstripTypeRankingFeatured.defaultProps = {
  ...AdComponentDefaultProps,
  type: null,
  cardClass: null,
};
